<template>
  <div
    v-show="!removed || (loading && !isCheckout)"
    :class="
      'row sidecart-product' +
      (line.pending_remove ? 'active' : '') +
      (line.pending_update ? 'updating' : '') +
      (line.justAdded ? 'just-added' : '')
    "
    :data-product-id="line.id"
  >
    <spinner v-if="loading && !isCheckout" />
    <div v-if="showBottleImage" class="product-image bottle-image">
      <picture data-not-lazy>
        <source :srcset="line.image_optimized_url" type="image/webp" />
        <source :srcset="line.image_url" type="image/jpeg" />
        <!-- No alt label for image since label (title) is already present below for screen readers -->
        <img :src="line.image_url" alt="" />
      </picture>
    </div>

    <div
      :class="{
        'product-content': true,
        'no-image': !showBottleImage,
      }"
    >
      <div class="product-message">Just Added</div>
      <div class="product-title" :class="{ 'w-90': !showBottleImage }">
        <div class="remove">
          <a
            data-gaevent="cart-remove"
            data-track="true"
            href="javascript:void(0)"
            :class="{ disabled: loading }"
            @click="updateLineQty(0)"
          >
            <template v-if="!showRemoveText">
              <label class="sr-only">Remove</label>
              <i class="icon-close"></i>
            </template>
            <template v-else> Remove </template>
          </a>
        </div>

        <div class="title">
          <span
            v-if="
              line.is_preorder &&
              line.first_ship_date &&
              !futuresCartItem &&
              isCheckout
            "
            class="preorder-available"
          >
            Preorder: Available {{ firstShippingFormatted(line) }}
          </span>
          <a
            :style="{ cursor: pdpLink ? 'pointer' : 'default' }"
            @click="openItem()"
          >
            {{ line.title }}
          </a>
          <div v-if="showMessage" class="product-message red">
            <div
              v-if="
                pptoOrder.status && pptoOrder.status.slug === 'pending_weather'
              "
            >
              By adding this wine, the first available delivery date of your
              order will be updated to {{ messageShippingFormatted }}. We will
              email you prior to shipment with a confirmed delivery date once we
              can safely ship your wine.
            </div>
            <div v-else-if="pptoOrder.is_as_you_go">
              By adding this wine, the first available delivery date of your
              order will be updated to {{ messageShippingFormatted }}. You will
              be able to adjust your delivery date, as needed, prior to
              shipment.
            </div>
            <div v-else>
              By adding this wine, the delivery date of your order will be
              updated to {{ messageShippingFormatted }}. You will be able to
              adjust your delivery date, as needed after you complete your
              purchase. To avoid having the ship date of your order moved, you
              may remove this wine from your cart.
            </div>
          </div>
        </div>

        <div class="reviews">
          <div
            v-if="
              !line.is_sampler && line.score.name && !line.score.hide_points
            "
          >
            <span class="score">{{ line.score.points }} points</span>&nbsp;
            <span class="reviewer">{{ line.score.name }}</span>
          </div>
        </div>
        <div
          v-if="
            line.is_preorder &&
            line.first_ship_date &&
            !futuresCartItem &&
            !isCheckout
          "
          class="item-preorder-available"
        >
          <span>Available {{ firstShippingFormatted(line) }}</span>
        </div>
        <div v-if="!isCheckout" class="item-cost-status">
          <div class="cost"><Price :amt="Number(line.unit_price)" /></div>
          <div v-if="line.is_preorder && !futuresCartItem" class="status">
            <span>Preorder</span>
          </div>
        </div>
        <div
          v-if="line.tiered_pricing_bottle_price_adjusted"
          class="item-preorder-available"
        >
          <span>{{ metadata.constance.PPTO_PRICE_ADJUSTED_TIER_MSG }}</span>
        </div>
      </div>
      <div class="product-message quantity-message">
        How many bottles would you like?
      </div>
      <div class="product-quantity">
        <div class="quantity-form">
          <div class="input-btl-overlay">
            <QuantityWidget
              :id="line.title"
              :quantity="line.quantity"
              :quantity-limit="
                quantityLimit ? line.quantity + quantityLimit : null
              "
              :available="true"
              :disabled="loading"
              @clear-errors="error = null"
              @set-quantity="updateLineQty"
            />
            <div class="input-btl-label">
              {{ line.is_sampler ? 'set' : 'btl' }}
            </div>
          </div>
          <button
            :disabled="quantity <= 0 || loading"
            class="change-quantity minus addToCart"
            @click="delayedUpdateLineQty(quantity - 1)"
          >
            &ndash;
          </button>
          <button
            v-if="quantityLimit === null || quantityLimit !== 0"
            :disabled="loading"
            class="change-quantity plus addToCart"
            @click="delayedUpdateLineQty(quantity + 1)"
          >
            +
          </button>

          <button
            :class="'button-apply ' + (loading ? 'spinner' : '')"
            :disabled="line.updating"
          ></button>
        </div>
        <div v-if="swapPriceAndCost" class="price">
          <Price :amt="Number(line.unit_price)" />
        </div>
        <div v-else class="price">
          <Price :amt="Number(line.price_excl_tax)" />
        </div>
        <div v-if="!line.is_discountable" class="excluded">
          Excluded from Promotional Discounts
        </div>
      </div>
    </div>
    <ul v-if="error && error.non_field_errors" class="errorlist">
      <li
        v-for="nf_error of error.non_field_errors"
        :key="nf_error"
        class="error"
      >
        Error: {{ nf_error }}
      </li>
    </ul>
    <ul v-else-if="error && error.reason" class="errorlist">
      <li class="error">Error: {{ error.reason }}</li>
    </ul>
  </div>
</template>

<script>
import {
  getBasket,
  getCheckoutBasket,
  getFuturesBasket,
  getPPTOBasket,
  updateLineCart,
} from '#common/services/checkoutServices'
import ga4 from '#common/utils/ga4'
import { baseURL } from '#common/utils/url'
import { mapGetters } from 'vuex'

export default {
  name: 'CartItem',
  components: {
    Spinner: defineAsyncComponent(
      () => import('#common/components/Loading/Spinner'),
    ),
    Price: defineAsyncComponent(() => import('#common/components/Price.vue')),
    QuantityWidget: defineAsyncComponent(
      () => import('@/components/Product/QuantityWidget'),
    ),
  },
  props: {
    line: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    toggleSideCartOnClick: {
      type: Boolean,
      required: false,
      default: true,
    },
    toggleSideCartOnUpdate: {
      type: Boolean,
      required: false,
      default: true,
    },
    showRemoveText: {
      type: Boolean,
      required: false,
      default: false,
    },
    isCheckout: {
      type: Boolean,
      required: false,
      default: false,
    },
    isPpto: {
      type: Boolean,
      required: false,
      default: false,
    },
    isFutures: {
      type: Boolean,
      required: false,
      default: false,
    },
    pdpLink: {
      type: Boolean,
      default: true,
    },
    quantityLimit: {
      type: Number,
      default: null,
    },
    futuresCartItem: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['loadingStart', 'loadingEnd'],
  data() {
    return {
      oldQuantity: null,
      loading: false,
      error: null,
      inputTimer: null,
      removed: false,
    }
  },
  computed: {
    ...mapGetters(['pptoOrder', 'pptoCart', 'cart']),
    quantity() {
      return this.line.quantity
    },
    swapPriceAndCost() {
      return this.isCheckout
    },
    showMessage() {
      if (this.line) {
        return (
          this.line.moves_delivery_date && this.line.latest_shipping_product
        )
      }
      return false
    },
    messageShippingFormatted() {
      return this.$moment(this.pptoCart.delivery_date).format('dddd, MMMM DD')
    },
    showBottleImage() {
      return (
        !this.isCheckout ||
        (this.line.rotate_for_insert && !this.line.is_sampler)
      )
    },
  },
  watch: {
    loading(newValue) {
      if (newValue) {
        this.$emit('loadingStart')
      } else {
        this.$emit('loadingEnd')
      }
    },
  },
  created() {
    this.oldQuantity = this.quantity
    this.metadata = useMetadata()
  },
  beforeUnmount() {
    if (this.loading) this.$emit('loadingEnd')
  },
  methods: {
    firstShippingFormatted(line) {
      return this.$moment(line.first_ship_date).format('M/D/YYYY')
    },
    delayedUpdateLineQty(quantity) {
      if (quantity === this.oldQuantity) {
        return
      }
      const quantityPayload = {
        quantity,
        index: this.index,
        line_id: this.line.id,
      }
      if (this.isPpto) {
        this.$store.dispatch('updatePPTOLineQuantity', quantityPayload)
      } else if (this.isFutures) {
        this.$store.dispatch('updateFuturesLineQuantity', quantityPayload)
      } else {
        this.$store.dispatch('updateLineQuantity', quantityPayload)
      }
      const period = 750
      clearTimeout(this.inputTimer)
      this.inputTimer = setTimeout(() => {
        this.updateLineQty(quantity)
      }, period)
    },
    async updateLineQty(quantity) {
      if (quantity === this.oldQuantity) {
        return
      }
      this.loading = true
      this.error = null
      if (quantity === 0) {
        this.removed = true
      }
      if (quantity > this.oldQuantity) {
        ga4.trackAddToCart({
          product: { ...this.line, quantity: quantity - this.oldQuantity },
        })
      } else if (quantity < this.oldQuantity) {
        ga4.trackRemoveFromCart({
          product: { ...this.line, quantity: this.oldQuantity - quantity },
        })
      }
      try {
        if (this.isPpto) {
          const addToCartData = {
            quantity: quantity - this.oldQuantity,
            product_id: this.line.product_id,
            source_url: baseURL() + this.$route.fullPath,
            post_purchase_order: this.pptoCart.post_purchase_order,
          }
          await this.$store.dispatch('addToPPTOCart', addToCartData)
        } else if (this.isFutures) {
          const addToCartData = {
            quantity: quantity - this.oldQuantity,
            product_id: this.line.product_id,
            isCheckout: this.isCheckout,
            source_url: baseURL() + this.$route.fullPath,
          }
          await this.$store.dispatch('addToFuturesCart', addToCartData)
        } else {
          await updateLineCart(
            this.$axios,
            {
              quantity,
              cart_id: this.cart.id,
              index: this.index,
              line_id: this.line.id,
              toggleCart: this.toggleSideCartOnUpdate,
              isCheckout: this.isCheckout,
              source_url: baseURL() + this.$route.fullPath,
            },
            this.$store,
          )
        }
        // TODO: WA-4857
        // eslint-disable-next-line vue/no-mutating-props
        this.line.quantity = quantity
        this.oldQuantity = quantity
      } catch (e) {
        /* eslint-disable */
        console.log(e)
        if (e.response) {
          this.error = e.response.data
        } else {
          // when 406 status, e is the error
          this.error = e
        }

        if (quantity < this.oldQuantity) {
          return
        }
        if (this.isPpto) {
          const basket = await getPPTOBasket(this.$axios)
          await this.$store.dispatch('updatePPTOCart', basket)
        } else if (this.isFutures) {
          const basket = await getFuturesBasket(this.$axios)
          await this.$store.dispatch('updateFuturesCart', basket)
        } else if (this.isCheckout) {
          const resetIcePack = false
          const updateDefaults = true
          const basket = await getCheckoutBasket(
            this.$axios,
            Sentry,
            resetIcePack,
            updateDefaults
          )
          await this.$store.dispatch('updateCart', basket)
        } else {
          const basket = await getBasket(this.$axios)
          await this.$store.dispatch('updateCart', basket)
        }
        this.removed = false
      } finally {
        this.loading = false
      }
    },
    openItem() {
      if (!this.pdpLink) {
        this.$emit('detail-clicked', this.line)
        return
      }
      this.$router.push(this.line.url)
      if (this.toggleSideCartOnClick) {
        this.$store.dispatch('toggleCart')
      }
    },
  },
}
</script>
